/**
 * Transaction origin for local Yjs mutations (used by UndoManager to track local changes)
 */
export const LOCAL_ORIGIN = Symbol('local-undo')

/**
 * Transaction origin for system-initiated Yjs mutations (e.g., generation completion).
 * Not tracked by UndoManager — these events should not be undoable.
 */
export const SYSTEM_ORIGIN = Symbol('system-event')

/**
 * Transaction origin for per-user Yjs mutations that should not be undoable
 * and should keep working in read-only sessions.
 */
export const PER_USER_ORIGIN = Symbol('per-user-event')

/**
 * Transaction origin for non-undoable editor mutations that should still be
 * blocked for viewers once the read-only gate is active.
 */
export const EDITOR_VOLATILE_ORIGIN = Symbol('editor-volatile-event')

/**
 * Ticks per second for internal timing (48kHz sample rate based).
 */
export const TICKS_PER_SECOND = TIMELINE_TICKS_PER_SECOND

/**
 * Frames per second for project frame rate (24fps standard).
 */
export const FRAMES_PER_SECOND = TIMELINE_FPS

/**
 * Ticks per frame (48,000 ticks/sec / 24 fps = 2,000 ticks/frame).
 */
export const TICKS_PER_FRAME = TIMELINE_TICKS_PER_FRAME

/**
 * Default max reference images before model metadata is hydrated into draft settings.
 * Keep canvas and generation panels aligned until model-specific limits are known.
 */
export const DEFAULT_MAX_REFERENCE_IMAGE_COUNT = 8

// Legacy exports for backwards compatibility (returns current server URLs)
// These are getters that return the current value on each access
import {
  formatCommentTimecode,
  getPlanTierRank,
  KNOWN_PLAN_IDS,
  parsePlanId,
  PLAN_TIER_RANK,
  planDisplayName,
  TIMELINE_FPS,
  TIMELINE_TICKS_PER_FRAME,
  TIMELINE_TICKS_PER_SECOND,
  type PlanFamily,
  type PlanTier,
} from '@/types/shared'
import { getApiUrl, getWsUrl } from './serverConfig'
import { sanitizeUnicodeFilename } from './unicodeFilename'
export const API_URL =
  typeof window === 'undefined' ? process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001' : getApiUrl()
export const WS_URL =
  typeof window === 'undefined' ? process.env.NEXT_PUBLIC_WS_URL || 'ws://localhost:3001/ws' : getWsUrl()

// TODO LH: this variable is a little sus and I don't fully understand why we need it
export const PROJECT_ID = process.env.NEXT_PUBLIC_PROJECT_ID || 'project-demo-v02'

/**
 * Important internal project IDs (sample, template, etc.)
 * Returns a label if the project is important, or null otherwise.
 */
export function getImportantProjectLabel(projectId: string): string | null {
  if (process.env.NEXT_PUBLIC_SAMPLE_PROJECT_ID && projectId === process.env.NEXT_PUBLIC_SAMPLE_PROJECT_ID) {
    return 'Sample'
  }
  if (process.env.NEXT_PUBLIC_TEMPLATE_PROJECT_ID && projectId === process.env.NEXT_PUBLIC_TEMPLATE_PROJECT_ID) {
    return 'Template'
  }
  return null
}

/**
 * Convert ticks to seconds
 */
export function ticksToSeconds(ticks: number): number {
  return ticks / TICKS_PER_SECOND
}

/**
 * Convert seconds to ticks
 */
export function secondsToTicks(seconds: number): number {
  return Math.round(seconds * TICKS_PER_SECOND)
}

/**
 * Format duration for display (e.g., "0:05" or "1:30")
 */
export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/**
 * Format timecode in SMPTE frame format (e.g., "00:00:05:12" for 5 seconds, 12 frames)
 * Format: HH:MM:SS:FF where FF is frame number (0-23 for 24fps)
 *
 * @deprecated Use formatTimecodeFromTicks for accurate frame display
 */
export function formatTimecode(seconds: number): string {
  // Convert to ticks first to avoid floating point precision issues
  const ticks = Math.round(seconds * TICKS_PER_SECOND)
  return formatTimecodeFromTicks(ticks)
}

/**
 * Format timecode from ticks (avoids floating point precision issues)
 * Format: MM:SS:FF where FF is frame number (0-23 for 24fps)
 * Hours are omitted since short-form content rarely exceeds an hour.
 */
export function formatTimecodeFromTicks(ticks: number): string {
  return formatCommentTimecode(ticks)
}

/**
 * Quantize ticks to nearest frame boundary.
 * Used to ensure playhead is always on a frame boundary when paused.
 */
export function quantizeToFrame(ticks: number): number {
  return Math.round(ticks / TICKS_PER_FRAME) * TICKS_PER_FRAME
}

// Routing the seconds↔frame conversion through ticks (via secondsToTicks's pre-round)
// absorbs sub-frame float drift in inputs like 9.999999s probed from media metadata —
// otherwise `Math.floor(9.999999 * 24)` shaves off a whole frame.

/**
 * Quantize seconds to the nearest 24fps frame boundary.
 */
export function quantizeSecondsToFrame(seconds: number): number {
  const ticks = secondsToTicks(seconds)
  return ticksToSeconds(Math.round(ticks / TICKS_PER_FRAME) * TICKS_PER_FRAME)
}

/**
 * Floor seconds down to the previous 24fps frame boundary.
 * Use for upper bounds that must not overshoot (e.g. clamping window length
 * against an unaligned source duration).
 */
export function floorSecondsToFrame(seconds: number): number {
  const ticks = secondsToTicks(seconds)
  return ticksToSeconds(Math.floor(ticks / TICKS_PER_FRAME) * TICKS_PER_FRAME)
}

/**
 * Ceil seconds up to the next 24fps frame boundary.
 * Use for lower bounds that must not undershoot (e.g. snapping a model's
 * fractional `minDurationSec` so we don't accidentally allow values below it).
 */
export function ceilSecondsToFrame(seconds: number): number {
  const ticks = secondsToTicks(seconds)
  return ticksToSeconds(Math.ceil(ticks / TICKS_PER_FRAME) * TICKS_PER_FRAME)
}

/**
 * Format seconds as `seconds:frames` (e.g. "07:20"). Both fields are zero-padded
 * to 2 digits; frames are 0-indexed at 24fps. Seconds is total seconds (no minutes
 * carry), so inputs ≥ 100s render with 3+ digits — fine for the short trim windows
 * this is used for. Use `formatTimecodeFromTicks` for `MM:SS:FF` if longer ranges
 * are in scope.
 */
export function formatSecondsAsTimecode(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00'
  const ticks = Math.round(seconds * TICKS_PER_SECOND)
  const totalFrames = Math.round(ticks / TICKS_PER_FRAME)
  const frame = totalFrames % FRAMES_PER_SECOND
  const totalSeconds = Math.floor(totalFrames / FRAMES_PER_SECOND)
  return `${totalSeconds.toString().padStart(2, '0')}:${frame.toString().padStart(2, '0')}`
}

/**
 * Shot color labels - pastel/muted tones for professional appearance
 */
export type ShotColorLabel = 'green' | 'yellow' | 'red' | 'blue' | 'purple'

/**
 * Tailwind swatch per color label — the dots users see on the canvas controls
 * and in settings. `COLOR_LABEL_MAP` (above) is the muted tint the engine and
 * the sidebar bars paint; keep the two in step when adding a color.
 */
export const COLOR_LABEL_SWATCH_CLASS: Record<ShotColorLabel, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-500',
  red: 'bg-red-500',
  blue: 'bg-blue-500',
  purple: 'bg-purple-500',
}

export const COLOR_LABEL_MAP: Record<ShotColorLabel, string> = {
  green: '#6cb887',
  yellow: '#d4b65c',
  red: '#d4736c',
  blue: '#6b9fd4',
  purple: '#a88bd4',
}

/**
 * Video aspect ratio (16:9) used throughout the app for program monitors.
 */
export const VIDEO_ASPECT_RATIO = 16 / 9

/**
 * Height of the program monitor controls bar (Scrubber + PlayerControls + padding).
 * Used to calculate the available video height within the timeline area.
 */
export const PROGRAM_MONITOR_CONTROLS_HEIGHT = 56

/**
 * Panel size constraints - unified configuration for resizable panels
 */
export const PANEL_CONSTRAINTS = {
  /** Timeline height (bottom panel) - sized so 16:9 video fills the default monitor width */
  timeline: { min: 120, max: 600, default: 248 },
  /** Left panel (shots sidebar in Create mode) */
  leftPanel: { min: 220, max: 600, default: 280 },
  /** Right panel (generation/detail panel in Create mode) */
  rightPanel: { min: 280, max: 450, default: 320 },
  /** Detail panel (shot details in Edit mode) */
  detailPanel: { min: 200, max: 600, default: 320 },
  /** Source/Program monitors in Edit mode (large video previews) */
  editMonitor: { min: 300, max: 1200, default: 480 },
  /** Monitor panel in timeline area (Create mode small preview) */
  timelineMonitor: { min: 280, max: 700, default: 340 },
  /** Sequences panel in timeline area (narrow list panel) */
  sequencesPanel: { min: 120, max: 350, default: 180 },
} as const

export type PanelConstraint = (typeof PANEL_CONSTRAINTS)[keyof typeof PANEL_CONSTRAINTS]

/**
 * Clamp a value within a panel's min/max constraints
 */
export function clampToConstraint(value: number, constraint: PanelConstraint): number {
  return Math.min(constraint.max, Math.max(constraint.min, value))
}

/**
 * Compute the ideal program monitor width for a given timeline height.
 * This ensures the 16:9 video fills the monitor panel exactly (no letterboxing).
 *
 * @param timelineHeight - The height of the bottom timeline area in pixels
 * @returns The ideal monitor width, clamped to timelineMonitor constraints
 */
export function computeIdealMonitorWidth(timelineHeight: number): number {
  const videoHeight = timelineHeight - PROGRAM_MONITOR_CONTROLS_HEIGHT
  const idealWidth = Math.round(videoHeight * VIDEO_ASPECT_RATIO)
  return clampToConstraint(idealWidth, PANEL_CONSTRAINTS.timelineMonitor)
}

/**
 * Timeline track constants
 */
export const DEFAULT_TRACK_COUNT = 2
export const MAX_TRACK_COUNT = 10

/**
 * Default timeline zoom level (pixels per second).
 * 67px/sec provides a good balance for 5-10 second clips.
 */
export const DEFAULT_PIXELS_PER_SECOND = 67

/**
 * Safety epsilon for timeline zoom calculations.
 * This is not a product zoom floor; it only avoids zero-scale geometry for
 * invalid inputs or sequences lasting longer than roughly 12 days at 1200px.
 */
export const MIN_TIMELINE_PIXELS_PER_SECOND = 0.001

/**
 * Drag threshold in pixels - minimum distance to move before showing drag feedback.
 * 2px provides tight click detection while still allowing drag initiation.
 */
export const DRAG_THRESHOLD_PX = 2

/**
 * Drag preview thumbnail dimensions - used for floating thumbnail during drag operations.
 * Small size (80px) for minimal visual footprint while dragging.
 */
export const DRAG_PREVIEW_WIDTH = 80
export const DRAG_PREVIEW_HEIGHT = Math.round(DRAG_PREVIEW_WIDTH / VIDEO_ASPECT_RATIO)

/**
 * Inset from the preview corner toward the cursor (screen px).
 *
 * Keeps the pointer slightly inside the thumbnail so the content stays visible
 * (proximity to the "tool" without covering the affordance — similar to 8–12dp
 * touch padding).
 */
export const DRAG_PREVIEW_CURSOR_INSET = 10

/**
 * Fixed `left` / `top` for portal drag ghosts so the cursor sits `inset` px inside the preview's top-left.
 */
export function getDragGhostFixedPosition(
  clientX: number,
  clientY: number,
  inset: number = DRAG_PREVIEW_CURSOR_INSET,
): { left: number; top: number } {
  return {
    left: clientX - inset,
    top: clientY - inset,
  }
}

/**
 * Placeholder parent ID for batch variations awaiting first shot completion.
 * Used in batch generation to indicate a variation's parent will be set after
 * the root shot is created.
 */
export const PENDING_BATCH_ROOT_PLACEHOLDER = '__PENDING_BATCH_ROOT__'

/**
 * Default duration for image clips on the timeline (in seconds).
 * Users can extend up to MAX_IMAGE_ASSET_DURATION_SECONDS via resize handles.
 */
export const DEFAULT_IMAGE_CLIP_DURATION_SECONDS = 2

/**
 * Maximum duration for image assets (in seconds).
 * This is the upper limit for how long an image clip can be on the timeline.
 */
export const MAX_IMAGE_ASSET_DURATION_SECONDS = 60

/**
 * Default duration for image clips in ticks.
 */
export const DEFAULT_IMAGE_CLIP_DURATION_TICKS = DEFAULT_IMAGE_CLIP_DURATION_SECONDS * TICKS_PER_SECOND

/**
 * Maximum duration for image assets in ticks.
 */
export const MAX_IMAGE_ASSET_DURATION_TICKS = MAX_IMAGE_ASSET_DURATION_SECONDS * TICKS_PER_SECOND

/**
 * Generate a download filename for an asset.
 * Matches the XML export naming convention: {paddedShotNumber}_{shortAssetId}.{extension}
 *
 * @param shotNumber - The shot number (will be zero-padded to 3 digits minimum)
 * @param assetId - The asset UUID (first 12 chars used for uniqueness)
 * @param extension - File extension (e.g., 'mp4', 'jpg', 'mp3'). Defaults to 'mp4' for backwards compatibility.
 * @returns Filename like "001_abc123def456.mp4" or "001_abc123def456.jpg"
 */
export function generateDownloadFilename(
  shotNumber: number | undefined,
  assetId: string,
  extension: string = 'mp4',
  customName?: string,
): string {
  // Determine padding based on shot number magnitude (match backend logic)
  const effectiveShotNumber = shotNumber ?? 0
  let padding = 3
  if (effectiveShotNumber >= 1000) padding = 4
  if (effectiveShotNumber >= 10000) padding = 5

  const paddedNumber = Math.max(0, effectiveShotNumber).toString().padStart(padding, '0')
  // Take first 12 chars of assetId (without dashes) for uniqueness
  const shortId = assetId.replace(/-/g, '').slice(0, 12)

  if (customName) {
    const safeName = sanitizeUnicodeFilename(customName).trim().replace(/\s+/g, '_')
    if (safeName) return `${paddedNumber}_${safeName}_${shortId}.${extension}`
  }

  return `${paddedNumber}_${shortId}.${extension}`
}

/**
 * Feature flags
 */
export const SHOW_MCP_SERVER_URL = true

/**
 * Subscription plan constants
 */
export const PLAN_PRO = 'pro'
export const PLAN_STUDIO = 'studio'
export const PLAN_ENTERPRISE = 'enterprise'

/** Tier ≥ Pro in either family (mirrors the backend's planPolicy). */
export const PLANS_WITH_PRIVATE_PROJECTS = new Set<string>(
  KNOWN_PLAN_IDS.filter(plan => getPlanTierRank(plan) >= PLAN_TIER_RANK.pro),
)

/** Customer-facing name of every known plan id (planDisplayName): "Pro", "Studio", … in both families. */
export const PLAN_DISPLAY_NAMES: Record<string, string> = Object.fromEntries(
  KNOWN_PLAN_IDS.map(plan => [plan, planDisplayName(plan) ?? plan]),
)

/** Olives a plan grants each month, by family and tier. Comp and annual variants grant their plan's. */
const MONTHLY_OLIVES_BY_FAMILY: Record<PlanFamily, Partial<Record<PlanTier, number>>> = {
  olives: { free: 0, pro: 300, studio: 1500 },
  byok: { pro: 50, studio: 250 },
}

/**
 * Monthly olive allocation per plan.
 * Enterprise allocations vary per customer (managed in Autumn), so not listed here.
 */
export const PLAN_MONTHLY_OLIVES: Record<string, number> = Object.fromEntries(
  KNOWN_PLAN_IDS.flatMap(plan => {
    const { family, tier } = parsePlanId(plan)
    const olives = MONTHLY_OLIVES_BY_FAMILY[family][tier]
    return olives === undefined ? [] : [[plan, olives] as const]
  }),
)

/**
 * Billing constants
 */

/** Maximum characters per V9 render (Kling element limit minus the source image). */
export const MAX_V9_CHARACTERS = 3

/** Maximum characters per V10 render. */
export const MAX_V10_CHARACTERS = 2

/** Set creation cost in olives — Fast mode. */
export const SET_CREATION_COST_FAST = 20
/** Set creation cost in olives — Quality mode. */
export const SET_CREATION_COST_QUALITY = 45

/** Top-up price per olive in USD. Uniform pricing — must match the Autumn olive_topup product configuration. */
export const OLIVE_PRICE_USD = 0.1

/**
 * The plan whose top-up bonus rate applies to a personal purchase — the
 * PERSONAL org's plan, only while its subscription is live. Mirrors the
 * backend's isActiveSubscriptionStatus gate (null status = legacy-active),
 * so the previewed bonus always matches the granted one. Team-lifted
 * effective plans deliberately do NOT set the personal rate.
 */
export function topupBonusPlan(plan?: string | null, status?: string | null): string | null {
  const active = status == null || status === 'active' || status === 'trialing' || status === 'past_due'
  return active ? (plan ?? null) : null
}

/** USD price for a top-up of the given olive quantity (uniform $0.10/olive). */
export function calculateTopupPriceUsd(quantity: number): number {
  return quantity * OLIVE_PRICE_USD
}

/** Format a USD amount for display (omit cents on whole dollars). */
export function formatUsdPrice(amount: number): string {
  if (amount % 1 === 0) return `$${amount.toLocaleString()}`
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Typeform URL for the Martini Film Grant application. */
export const FILM_GRANT_FORM_URL = 'https://form.typeform.com/to/WfS3xh0I'

/**
 * Discord community invite. Single source for every entry point (Learn header,
 * Learn sidebar, tutorials card, docs-chat fallback, account menu, footer,
 * motion onboarding render room).
 */
export const DISCORD_INVITE_URL = 'https://discord.com/invite/bXNtjKpVna'

/**
 * Billing display utilities
 *
 * All costs in the system are expressed in olives (user-facing credit unit).
 * The backend model handlers define costs directly in olives.
 * USD is only used internally for margin tracking (not for display or billing).
 */

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 GB", "256 MB", "128 KB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
  }
  if (bytes >= 1024 * 1024) {
    return `${Math.round(bytes / (1024 * 1024))} MB`
  }
  return `${Math.round(bytes / 1024)} KB`
}

/**
 * Format olive cost for display
 * @param olives - Number of olives
 * @returns Formatted string with commas (e.g., "1,234.5" or "10")
 */
export function formatOlives(olives: number, opts?: { floor?: boolean }): string {
  const value = opts?.floor ? Math.floor(olives) : olives
  // Show up to 2 decimal places, but remove trailing zeros for a cleaner look
  // e.g., 10.00 -> "10", 1.40 -> "1.4", 1.12 -> "1.12"
  // Use toLocaleString to add thousands separators (commas)
  const formatted = parseFloat(value.toFixed(2))
  return formatted.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

/**
 * Sidebar/panel list layout constants - shared between Shots, Sequences, Scenes panels
 * All values in pixels
 */
export const SIDEBAR_LAYOUT = {
  /** Left padding for chevron area (small, tucked) */
  chevronPadding: 4,
  /** Width of chevron + gap before content */
  chevronAreaWidth: 16,
  /** Indent size per nesting level */
  indentSize: 20,
  /** Right padding for items */
  rightPadding: 12,
} as const

/** Row thumbnail in the Media panel lists (Generated, Uploaded, Off canvas), in CSS px.
 *  Used for both the rendered box and the preview request so the two never drift. */
export const SIDEBAR_THUMBNAIL = {
  width: 48,
  height: 32,
} as const

/**
 * Calculate left padding for a sidebar list item based on depth
 * Items start with chevronPadding, content aligns after chevronAreaWidth
 */
export function getSidebarItemPadding(depth: number): number {
  return SIDEBAR_LAYOUT.chevronPadding + depth * SIDEBAR_LAYOUT.indentSize
}

/**
 * Get the padding for panel headers to align with item content (icons/text, not chevrons)
 */
export function getSidebarHeaderPadding(): number {
  return SIDEBAR_LAYOUT.chevronPadding + SIDEBAR_LAYOUT.chevronAreaWidth
}
